import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import { useAlert } from "../../../Hooks/useAlert";
import { customerAPI, milkRateAPI } from "../../../services/api";
import settingsLanguage from "../../../language/settingsLanguage";
import Alert from "../../common/Alert";
import BackButton from "./BackButton";

function AddCustomerPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const text = settingsLanguage[language];
  const { showAlert, alertConfig } = useAlert();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [customerId, setCustomerId] = useState(0);
  const [customerName, setCustomerName] = useState("");
  const [whatsAppNo, setWhatsAppNo] = useState("");
  const [milkType, setMilkType] = useState("Cow");
  const [useMasterRate, setUseMasterRate] = useState(true);
  const [cowRate, setCowRate] = useState(55);
  const [buffaloRate, setBuffaloRate] = useState(65);
  const [cowDefaultLiters, setCowDefaultLiters] = useState(1);
  const [buffaloDefaultLiters, setBuffaloDefaultLiters] = useState(0.5);

  const [masterCowRate, setMasterCowRate] = useState(55);
  const [masterBuffaloRate, setMasterBuffaloRate] = useState(65);

  const [nameError, setNameError] = useState("");
  const [whatsappError, setWhatsappError] = useState("");
  const [customerList, setCustomerList] = useState([]);

  useEffect(() => {
    loadMilkRates();
    loadCustomerList();
  }, []);

  useEffect(() => {
    const editCustomer = location.state?.editCustomer;
    if (editCustomer) {
      setIsEditMode(true);
      setCustomerId(editCustomer.customerId || 0);
      setCustomerName(editCustomer.customerName || "");
      setWhatsAppNo(editCustomer.whatsAppNo || "");
      setMilkType(editCustomer.milkType === 0 ? "Cow" : editCustomer.milkType === 1 ? "Buffalo" : "Cow");
      setUseMasterRate(editCustomer.useMasterRate !== false);
      if (editCustomer.cowRate != null) setCowRate(editCustomer.cowRate);
      if (editCustomer.buffaloRate != null) setBuffaloRate(editCustomer.buffaloRate);
      if (editCustomer.cowDefaultLiters != null) setCowDefaultLiters(editCustomer.cowDefaultLiters);
      if (editCustomer.buffaloDefaultLiters != null) setBuffaloDefaultLiters(editCustomer.buffaloDefaultLiters);
    }
  }, [location.state?.editCustomer]);

  const handleBack = () => {
    if (isEditMode) {
      navigate("/settings/customer/list");
    } else {
      navigate("/settings", { state: { openSection: "customer" } });
    }
  };

  useEffect(() => {
    if (customerName && customerName.trim().length >= 2) {
      const isDuplicate = customerList.some(
        (c) =>
          c.customerName.toLowerCase() === customerName.toLowerCase().trim() &&
          c.customerId !== customerId
      );
      if (!isDuplicate) {
        setNameError("");
      } else {
        setNameError("Customer with this name already exists");
      }
    } else if (customerName && customerName.trim().length < 2) {
      setNameError("Customer name must be at least 2 characters");
    } else if (!customerName || !customerName.trim()) {
      setNameError("Customer name is required");
    }
  }, [customerName, customerList, customerId]);

  useEffect(() => {
    if (whatsAppNo) {
      const whatsappRegex = /^[6-9]\d{9}$/;
      const cleanNumber = whatsAppNo.replace(/\D/g, "");
      if (whatsappRegex.test(cleanNumber)) {
        setWhatsappError("");
      } else if (cleanNumber.length > 0) {
        setWhatsappError("Enter valid 10-digit WhatsApp number (starting with 6-9)");
      }
    } else {
      setWhatsappError("");
    }
  }, [whatsAppNo]);

  const loadMilkRates = async () => {
    try {
      const response = await milkRateAPI.getMilkRates();
      if (response.success) {
        setMasterCowRate(response.data.cowRate);
        setMasterBuffaloRate(response.data.buffaloRate);
        setCowRate(response.data.cowRate);
        setBuffaloRate(response.data.buffaloRate);
      }
    } catch (error) {
      console.error("Error loading milk rates:", error);
    }
  };

  const loadCustomerList = async () => {
    try {
      const response = await customerAPI.getCustomers();
      if (response.success) {
        setCustomerList(response.data);
      }
    } catch (error) {
      console.error("Error loading customer list:", error);
    }
  };

  const resetForm = () => {
    setCustomerId(0);
    setCustomerName("");
    setWhatsAppNo("");
    setMilkType("Cow");
    setCowRate(masterCowRate);
    setBuffaloRate(masterBuffaloRate);
    setCowDefaultLiters(1);
    setBuffaloDefaultLiters(0.5);
    setUseMasterRate(true);
    setNameError("");
    setWhatsappError("");
  };

  const handleSaveCustomer = async () => {
    setNameError("");
    setWhatsappError("");

    if (!customerName?.trim()) {
      setNameError("Customer name is required");
      return;
    }
    if (customerName.trim().length < 2) {
      setNameError("Customer name must be at least 2 characters");
      return;
    }

    if (whatsAppNo) {
      const whatsappRegex = /^[6-9]\d{9}$/;
      const cleanNumber = whatsAppNo.replace(/\D/g, "");
      if (!whatsappRegex.test(cleanNumber)) {
        setWhatsappError("Enter valid 10-digit WhatsApp number (starting with 6-9)");
        return;
      }
    }

    const existingCustomer = customerList.find(
      (c) =>
        c.customerName.toLowerCase() === customerName.toLowerCase().trim() &&
        c.customerId !== customerId
    );
    if (existingCustomer) {
      setNameError("Customer with this name already exists");
      return;
    }

    setLoading(true);
    try {
      const milkTypeEnum = milkType === "Cow" ? 0 : milkType === "Buffalo" ? 1 : 2;
      const customerData = {
        customerId,
        customerName: customerName.trim(),
        whatsAppNo: whatsAppNo?.trim() || null,
        milkType: milkTypeEnum,
        cowRate: useMasterRate ? null : cowRate,
        buffaloRate: useMasterRate ? null : buffaloRate,
        cowDefaultLiters: milkType === "Cow" ? cowDefaultLiters : null,
        buffaloDefaultLiters: milkType === "Buffalo" ? buffaloDefaultLiters : null,
        useMasterRate,
      };

      let response;
      if (customerId === 0) {
        response = await customerAPI.addCustomer(customerData);
        if (response.success) {
          await showAlert({
            type: "success",
            title: "Success",
            message: "Customer added successfully!",
          });
          resetForm();
          setIsEditMode(false);
        } else {
          await showAlert({
            type: "error",
            title: "Error",
            message: response.message || "Failed to add customer",
          });
        }
      } else {
        response = await customerAPI.updateCustomer(customerData);
        if (response.success) {
          await showAlert({
            type: "success",
            title: "Success",
            message: "Customer updated successfully!",
          });
        } else {
          await showAlert({
            type: "error",
            title: "Error",
            message: response.message || "Failed to update customer",
          });
        }
      }
    } catch (error) {
      console.error("Error saving customer:", error);
      await showAlert({
        type: "error",
        title: "Error",
        message: "Error saving customer. Please check your input.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <header className="settings-page-header">
        <button type="button" onClick={handleBack} className="settings-back-btn">
          <i className="fas fa-arrow-left" />
          <span>{text.back}</span>
        </button>
        <h1 className="settings-page-title">{isEditMode ? text.editCustomerPageTitle : text.addCustomerPageTitle}</h1>
      </header>

      <main className="settings-page-content">
        <div className="settings-form-card">
          <div className="settings-form-body">
            <div className="settings-form-group">
              <label className="settings-form-label">{text.customerName} *</label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className={`settings-form-input ${nameError ? "input-error" : ""}`}
                placeholder={text.enterCustomerName}
              />
              {nameError && <p className="settings-form-error">{nameError}</p>}
            </div>

            <div className="settings-form-group">
              <label className="settings-form-label">{text.whatsappNo}</label>
              <input
                type="tel"
                value={whatsAppNo}
                onChange={(e) => setWhatsAppNo(e.target.value)}
                className={`settings-form-input ${whatsappError ? "input-error" : ""}`}
                placeholder={text.whatsappPlaceholder}
              />
              {whatsappError && <p className="settings-form-error">{whatsappError}</p>}
            </div>

            <div className="settings-form-group">
              <label className="settings-form-label">{text.milkType}</label>
              <select
                value={milkType}
                onChange={(e) => setMilkType(e.target.value)}
                className="settings-form-input settings-form-select"
              >
                <option value="Cow">{text.milkTypeCow}</option>
                <option value="Buffalo">{text.milkTypeBuffalo}</option>
              </select>
            </div>

            <div className="settings-form-group">
              <label className="settings-form-checkbox">
                <input
                  type="checkbox"
                  checked={useMasterRate}
                  onChange={(e) => setUseMasterRate(e.target.checked)}
                />
                <span>{text.useMasterRate}</span>
              </label>
            </div>

            {!useMasterRate && milkType === "Cow" && (
              <div className="settings-form-group">
                <label className="settings-form-label">{text.cowRate}</label>
                <input
                  type="number"
                  step="0.01"
                  value={cowRate === 0 ? "" : cowRate}
                  onChange={(e) =>
                    setCowRate(e.target.value === "" ? "" : parseFloat(e.target.value))
                  }
                  className="settings-form-input"
                />
              </div>
            )}

            {milkType === "Cow" && (
              <div className="settings-form-group">
                <label className="settings-form-label">{text.cowDefaultLiters}</label>
                <input
                  type="number"
                  step="0.1"
                  value={cowDefaultLiters === 0 ? "" : cowDefaultLiters}
                  onChange={(e) =>
                    setCowDefaultLiters(e.target.value === "" ? "" : parseFloat(e.target.value))
                  }
                  className="settings-form-input"
                />
              </div>
            )}

            {!useMasterRate && milkType === "Buffalo" && (
              <div className="settings-form-group">
                <label className="settings-form-label">{text.buffaloRate}</label>
                <input
                  type="number"
                  step="0.01"
                  value={buffaloRate === 0 ? "" : buffaloRate}
                  onChange={(e) =>
                    setBuffaloRate(e.target.value === "" ? "" : parseFloat(e.target.value))
                  }
                  className="settings-form-input"
                />
              </div>
            )}

            {milkType === "Buffalo" && (
              <div className="settings-form-group">
                <label className="settings-form-label">{text.buffaloDefaultLiters}</label>
                <input
                  type="number"
                  step="0.1"
                  value={buffaloDefaultLiters === 0 ? "" : buffaloDefaultLiters}
                  onChange={(e) =>
                    setBuffaloDefaultLiters(e.target.value === "" ? "" : parseFloat(e.target.value))
                  }
                  className="settings-form-input"
                />
              </div>
            )}

            <div className="settings-form-actions">
              <button
                type="button"
                onClick={handleSaveCustomer}
                disabled={loading || !!nameError || !!whatsappError}
                className="settings-form-btn"
              >
                <i className="fas fa-save" />
                <span>{loading ? text.saving : isEditMode ? text.updateCustomer : text.addCustomer}</span>
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="settings-form-btn settings-form-btn-secondary"
              >
                <i className="fas fa-redo" />
                <span>{text.reset}</span>
              </button>
            </div>
          </div>
        </div>
      </main>

      {alertConfig && <Alert {...alertConfig} />}
    </div>
  );
}

export default AddCustomerPage;
